import { Alert, Modal, Spin } from 'antd';
import React, { useRef } from 'react';

import { type AssessmentFlowStep } from '@/components/assessment/AssessmentStepPager';
import { TranscriptAnalysisReview } from '@/components/assessment/TranscriptAnalysisReview';
import {
  CrepeMarkdownField,
  type CrepeMarkdownFieldHandle,
} from '@/components/editor/CrepeMarkdownField';
import { useAssessmentStep2 } from '@/hooks/useAssessmentStep2';
import { parseTranscriptAnalysisMarkdown } from '@/utils/transcriptAnalysisReviewParser';

export interface AssessmentStep2ReviewProps {
  assessmentInstanceId: string;
  clientId: string;
  projectId: string;
  accessToken: string | null;
  clientLabel?: string;
  onNavigateBack?: () => void;
  onNavigateForward?: () => void;
  onNavigateAssessmentStep?: (step: AssessmentFlowStep) => void;
}

function downloadBlob(markdown: string, filename: string): void {
  const blob = new Blob([markdown], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export const AssessmentStep2Review: React.FC<AssessmentStep2ReviewProps> = ({
  assessmentInstanceId,
  clientId,
  projectId,
  accessToken,
  clientLabel = '',
  onNavigateBack,
  onNavigateForward,
  onNavigateAssessmentStep,
}) => {
  const editorRef = useRef<CrepeMarkdownFieldHandle | null>(null);
  const {
    artifact,
    validation,
    loading,
    error,
    editMode,
    editValue,
    saving,
    approving,
    setEditMode,
    setEditValue,
    handleEdit,
    handleSave,
    handleApprove,
  } = useAssessmentStep2(assessmentInstanceId, accessToken, clientId, projectId, onNavigateForward);

  if (loading) {
    return <Spin size="large" style={{ display: 'block', marginTop: 64 }} />;
  }

  if (error || !artifact) {
    return (
      <Alert
        type="error"
        message={error ?? 'Artifact not found'}
        description="Return to Step 2 to run the analysis."
        showIcon
      />
    );
  }

  const model = parseTranscriptAnalysisMarkdown(artifact.markdown, '02-themes-and-opps.md');
  const isApproved = artifact.approval_state === 'approved';
  const approvalDisabled = isApproved || !(validation?.passed ?? false) || approving;
  let busyAction: 'edit' | 'approve' | null = null;
  if (saving) {
    busyAction = 'edit';
  } else if (approving) {
    busyAction = 'approve';
  }

  return (
    <>
      <TranscriptAnalysisReview
        markdown={artifact.markdown}
        model={model}
        clientLabel={clientLabel}
        approvalDisabled={approvalDisabled}
        busyAction={busyAction}
        currentFlowStep="2-review"
        onNavigateAssessmentStep={onNavigateAssessmentStep}
        onDownload={downloadBlob}
        onEdit={handleEdit}
        onBack={() => onNavigateBack?.()}
        onApprove={() => void handleApprove()}
      />
      <Modal
        title="Edit analysis Markdown"
        open={editMode}
        onCancel={() => setEditMode(false)}
        onOk={() => void handleSave()}
        okText="Save"
        confirmLoading={saving}
        width={960}
        destroyOnHidden
      >
        <CrepeMarkdownField
          ref={editorRef}
          bootKey={`${assessmentInstanceId}-analysis-edit`}
          defaultMarkdown={editValue}
          onMarkdownChange={setEditValue}
          minHeight={520}
        />
      </Modal>
    </>
  );
};

export default AssessmentStep2Review;
